import {ChatBubbleLeftRightIcon} from "@heroicons/react/20/solid";
import {IconBell, IconCalendarRepeat, IconFlag} from "@tabler/icons-react";
import {ReactNode, useMemo, useState} from "react";
import {OtsAvatar} from "../../components/OtsAvatar";
import {OtsBadge} from "../../components/OtsBadge";
import {OtsActionButton} from "../../components/OtsButton";
import {OtsDateTime} from "../../components/OtsDateTime";
import {OtsSegmentedControl} from "../../components/OtsSegmentedControl";
import {DispatchActivityItem, DispatchActivityKind} from "./dispatch-profile-utils";

export type DispatchActivityFilter = "all" | DispatchActivityKind;

export type DispatchActivityLogProps = {
  items: DispatchActivityItem[];
  loading?: boolean;
  error?: boolean;
  density?: "compact" | "default";
  /** Shows the add comment action. */
  onAddComment?: () => void;
  /** Formats a day heading; defaults to the viewer's locale. Injectable for stable tests. */
  formatDay?: (dttm: number) => string;
};

const filterLabels: Record<DispatchActivityFilter, string> = {
  all: "All",
  comment: "Comments",
  carryover: "Carryovers",
  lifecycle: "Status",
  notification: "Notifications",
};

const defaultFormatDay = (dttm: number) =>
  new Intl.DateTimeFormat(undefined, {weekday: "short", month: "short", day: "numeric", year: "numeric"}).format(dttm);

const isInternal = (messageType?: string) => messageType?.trim().toUpperCase() === "INTERNAL";

const ItemIcon = ({item}: {item: DispatchActivityItem}) => {
  if (item.kind === "comment") return <OtsAvatar name={item.author} size="sm" decorative/>;

  const icon: ReactNode = item.kind === "carryover" ? <IconCalendarRepeat/> : item.kind === "notification" ? <IconBell/> : <IconFlag/>;
  return (
    <span
      aria-hidden="true"
      className={[
        "flex size-8 shrink-0 items-center justify-center rounded-full border [&>svg]:size-4",
        item.kind === "carryover"
          ? "border-[var(--ots-warning-outline)] bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]"
          : item.tone === "danger"
          ? "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]"
          : item.kind === "notification"
          ? "border-[var(--ots-outline-variant)] bg-[var(--ots-primary-container)] text-[var(--ots-primary)]"
          : "border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)] text-[var(--ots-text-muted)]",
      ].join(" ")}>
      {icon}
    </span>
  );
};

/** Comments, carryovers and status changes of a dispatch in one dated feed, newest first. */
export const DispatchActivityLog = ({
  items,
  loading = false,
  error = false,
  density = "default",
  onAddComment,
  formatDay = defaultFormatDay,
}: DispatchActivityLogProps) => {
  const [filter, setFilter] = useState<DispatchActivityFilter>("all");

  const counts = useMemo(() => {
    const result: Record<DispatchActivityFilter, number> = {all: items.length, comment: 0, carryover: 0, lifecycle: 0, notification: 0};
    items.forEach(item => result[item.kind]++);
    return result;
  }, [items]);

  const days = useMemo(() => {
    const groups: {day: string; items: DispatchActivityItem[]}[] = [];
    items
      .filter(item => filter === "all" || item.kind === filter)
      .forEach(item => {
        const day = formatDay(item.dttm);
        const last = groups[groups.length - 1];
        if (last?.day === day) last.items.push(item);
        else groups.push({day, items: [item]});
      });
    return groups;
  }, [filter, formatDay, items]);

  const gap = density === "compact" ? "gap-3" : "gap-4";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <OtsSegmentedControl<DispatchActivityFilter>
          ariaLabel="Filter dispatch activity"
          size="compact"
          value={filter}
          onChange={setFilter}
          segments={(Object.keys(filterLabels) as DispatchActivityFilter[]).map(id => ({
            id,
            label: filterLabels[id],
            count: counts[id],
          }))}/>
        {onAddComment && (
          <OtsActionButton
            size={density === "compact" ? "compact" : "default"}
            variant="secondary"
            icon={<ChatBubbleLeftRightIcon/>}
            onClick={onAddComment}>
            Add comment
          </OtsActionButton>
        )}
      </div>

      {loading && <p role="status" className="mt-5 text-sm text-[var(--ots-text-muted)]">Loading activity…</p>}

      {!loading && error && (
        <p role="alert" className="mt-5 rounded-md border border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] px-3 py-2 text-sm text-[var(--ots-on-danger-container)]">
          Some activity could not be loaded.
        </p>
      )}

      {!loading && !days.length && (
        <div className="mt-5 rounded-md border border-dashed border-[var(--ots-outline)] bg-[var(--ots-background)] px-5 py-10 text-center text-sm text-[var(--ots-text-muted)]">
          {filter === "comment" ? "No comments have been added yet." : "No activity has been recorded yet."}
        </div>
      )}

      {!loading && !!days.length && (
        <ol aria-label="Dispatch activity" className={density === "compact" ? "mt-4 space-y-5" : "mt-6 space-y-7"}>
          {days.map(group => (
            <li key={group.day}>
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">{group.day}</h3>
              <ol className="space-y-3">
                {group.items.map(item => (
                  <li key={item.id} className={`flex ${gap}`}>
                    <ItemIcon item={item}/>
                    <div className={[
                      "min-w-0 flex-1",
                      item.kind === "comment"
                        ? "rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] px-3 py-2.5"
                        : "py-1",
                    ].join(" ")}>
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-[var(--ots-secondary)]">{item.title}</span>
                        {item.kind === "comment" && isInternal(item.messageType) && (
                          <OtsBadge tone="warning" size="compact">Internal</OtsBadge>
                        )}
                        <span className="text-xs text-[var(--ots-text-muted)]"><OtsDateTime value={item.dttm} format="time"/></span>
                      </div>
                      {item.body && (
                        <p className={[
                          "mt-1 whitespace-pre-wrap break-words text-sm",
                          item.kind === "comment" ? "text-[var(--ots-text)]" : "text-[var(--ots-text-muted)]",
                        ].join(" ")}>
                          {item.body}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
